import React, {memo, useEffect, useMemo, useState} from 'react';
import {HiOutlineUsers} from 'react-icons/hi';
import {Resource} from '../../Resource/types';
import PeopleCard from './PeopleCard';
import PeopleModal from './PeopleModal';
import ReInviteModal from '../../../components/Modals/ReInviteModal';
import DeactivateConfirmationModal from '../../../components/Modals/DeactivateConfirmationModal';
import useOrganizationService from '../../../services/organization';
import useResourcesService from '../../../services/resources';
import {useJwt} from '../../../helpers/common';
import {errorHandler} from '../../../utils/errorHandler';
import {UserRole} from "../../../types/manageUsers";
import useAzureService from '../../../services/azure';
import {azureTeamSyncRepository} from '../../../services/repositories';
import { useUrlFilters } from '../../../hooks/useUrlFilters';

interface PeopleContentProps {
  resources: Resource[];
  searchTerm?: string;
  error?: any;
}

const PeopleContent: React.FC<PeopleContentProps> = memo(({ 
  resources,
  searchTerm = '',
  error = null,
}) => {
  const [showPeopleModal, setShowPeopleModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showOnlyActive, setShowOnlyActive] = useState<boolean>(false);
  const [showReInviteModal, setShowReInviteModal] = useState(false);
  const [reinviteTarget, setReinviteTarget] = useState<Resource | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<Resource | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [hideImported, setHideImported] = useState<boolean>(false);

  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const user_id = JWTdata.user_id;
  const { inviteUsers, fetchPendingInvites, fetchTeams } = useOrganizationService();
  const { deactivateResource } = useResourcesService();
  const { fetchAzureTeamSyncDifferences } = useAzureService();

  const SHOW_ONLY_ACTIVE_PEOPLE_STORAGE_KEY = 'cadence_people_show_only_active';
  const HIDE_IMPORTED_PEOPLE_STORAGE_KEY = 'cadence_people_hide_imported';

  const { filters: urlFilters, setFilter } = useUrlFilters([
    { urlKey: 'id', storageKey: 'res_manage_id', defaultValue: '' }
  ]);

  // Sync URL parameter to local state for deep linking
  useEffect(() => {
    if (urlFilters.id && resources && resources.length > 0) {
      const personId = urlFilters.id as string;
      const resource = resources.find(r => String(r.id) === personId);
      
      if (resource) {
        setSelectedResource(resource);
        setShowPeopleModal(true);
      } else {
        // If the ID in URL is invalid, clear the state and URL
        setSelectedResource(null);
        setShowPeopleModal(false);
        setFilter('id', '');
      }
    } else if (!urlFilters.id) {
      // Handle the back button if URL clears the param
      setSelectedResource(null);
      setShowPeopleModal(false);
    }
  }, [urlFilters.id, resources, setFilter]);

  useEffect(() => {
    const storedValue = localStorage.getItem(SHOW_ONLY_ACTIVE_PEOPLE_STORAGE_KEY);
    if (storedValue !== null) {
      setShowOnlyActive(storedValue === 'true');
    }

    const storedValue2 = localStorage.getItem(HIDE_IMPORTED_PEOPLE_STORAGE_KEY);
    if (storedValue2 !== null) {
      setHideImported(storedValue2 === 'true');
    }
  }, []);

  const handleToggleShowOnlyActive = () => {
    setShowOnlyActive(prev => {
      const next = !prev;
      localStorage.setItem(SHOW_ONLY_ACTIVE_PEOPLE_STORAGE_KEY, String(next));
      return next;
    });
  };

  const handleToggleHideImported = () => {
    setHideImported(prev => {
      const next = !prev;
      localStorage.setItem(HIDE_IMPORTED_PEOPLE_STORAGE_KEY, String(next));
      return next;
    });
  };

  const filteredResources = useMemo(() => {
    let list = resources ? resources.slice() : [];
    if (showOnlyActive) {
      list = list.filter((r: Resource) => !r.is_deleted);
    }
    if (hideImported) {
      list = list.filter((r: Resource) => !r.imported);
    }
    return list.sort((a: Resource, b: Resource) => {
      if (!!a.is_deleted === !!b.is_deleted) {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
      return a.is_deleted ? 1 : -1; 
    });
  }, [resources, showOnlyActive, hideImported]);

  const handleRequestReinvite = (resource: Resource) => {
    setReinviteTarget(resource);
    setShowReInviteModal(true);
  };

  const handleConfirmReInvite = async () => {
    if (!org_id || !reinviteTarget?.email) return;
    try {
      setInviteLoading(true);
      const payload = {
        email_list: [{
          email: reinviteTarget.email,
          role: reinviteTarget.user_role || UserRole.BASIC_MEMBER
        }]
      };
      await inviteUsers(org_id, payload);
      setShowReInviteModal(false);
      setReinviteTarget(null);
      await fetchPendingInvites(org_id);
    } catch (error) {
      errorHandler.showError(error);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRequestDeactivate = (resource: Resource) => {
    setDeactivateTarget(resource);
    setShowDeactivateModal(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!org_id || !deactivateTarget?.id) return;
    try {
      setDeactivateLoading(true);
      await deactivateResource(org_id, String(deactivateTarget.id));
      await fetchTeams(org_id);
      
      // Reset IndexedDB cache and refresh team sync differences after deactivation
      if (org_id && user_id) {
        await azureTeamSyncRepository.clearCachedData(org_id, user_id);
        await fetchAzureTeamSyncDifferences(org_id, user_id);
      }
      
      // Close modal after all operations complete
      setShowDeactivateModal(false);
      setDeactivateTarget(null);
    } catch (error) {
      errorHandler.showError(error);
    } finally {
      setDeactivateLoading(false);
    }
  };

  if (error) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-2xl mb-6">
          <HiOutlineUsers className="w-10 h-10 text-red-500" />
        </div>
        <h3 className="text-xl font-bold text-light-text-primary dark:text-light-text-inverse mb-2">
          Unable to Load People
        </h3>
        <p className="text-light-text-secondary dark:text-light-text-muted">
          Something went wrong. Please try refreshing the page.
        </p>
      </div>
    );
  }

  if (!resources || resources.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl mb-6">
          <HiOutlineUsers className="w-10 h-10 text-blue-500" />
        </div>
        <h3 className="text-xl font-bold text-light-text-primary dark:text-light-text-inverse mb-2">
          No People Yet
        </h3>
        <p className="text-light-text-secondary dark:text-light-text-muted max-w-md mx-auto">
          Your organization doesn't have any people yet. They will appear here once they're added.
        </p>
      </div>
    );
  }

  return (
    <>      
      <div className="min-h-screen rounded-xl dark:bg-secondary-dark">        
        {/* Member Overview Stats */}
        <div className="px-6 py-6">
          {/* People Grid */}
          <div>
            <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {searchTerm 
                  ? `Found ${filteredResources.length} ${filteredResources.length === 1 ? 'person' : 'people'}` 
                  : `All People (${filteredResources.length})`}
              </h3>
              {/* Inline filters (two checkboxes side by side) */}
              <div className="flex items-center ml-4 gap-8">
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    checked={showOnlyActive}
                    onChange={handleToggleShowOnlyActive}
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-100">Exclude deactivated people</span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    checked={hideImported}
                    onChange={handleToggleHideImported}
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-100">Exclude imported people</span>
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6 items-stretch">
            {filteredResources
              .map((resource: Resource, index: number) => (
                <PeopleCard
                  index={index}
                  key={resource.id}
                  resource={resource}
                  handleCardClick={() => {
                    setFilter('id', String(resource.id));
                    // The useEffect above will handle opening the modal
                  }}
                  onRequestReinvite={handleRequestReinvite}
                  onRequestDeactivate={handleRequestDeactivate}
                />
              ))}
            </div>
          </div>

          {/* No search results */}
          {searchTerm && resources.length === 0 && (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 rounded-2xl mb-4">
                <HiOutlineUsers className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No People Match Your Search
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                Try adjusting your search terms or explore all people by clearing the search.
              </p>
            </div>
          )}
        </div>
      </div>

      {selectedResource && (
        <PeopleModal
          isVisible={showPeopleModal}
          onClose={() => setFilter('id', '')}
          resource={selectedResource}
        />
      )}

      <ReInviteModal
        show={showReInviteModal}
        onClose={() => setShowReInviteModal(false)}
        name={reinviteTarget?.name || ''}
        email={reinviteTarget?.email || ''}
        onConfirm={handleConfirmReInvite}
        loading={inviteLoading}
      />

      <DeactivateConfirmationModal
        show={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        name={deactivateTarget?.name || ''}
        email={deactivateTarget?.email || ''}
        onConfirm={handleConfirmDeactivate}
        loading={deactivateLoading}
      />
    </>
  );
});

export default PeopleContent; 